/**
 * HTTP service for the intakes feature (PX-W7.1, TASK-A30F4433): the version's
 * intakes and their per-assignee sessions, the conversation runtime reads,
 * and the org-admin meeting-capture surface. One transport, reads only —
 * every success body is BARE (`JsonResults.ok` has no envelope).
 */

import { apiClient } from '@/services'
import type {
  AttributionView,
  CaptureLinkRow,
  ConversationSnapshot,
  ExternalCaptureRow,
  IntakeRow,
  MeetingChannelSurface,
  ProgressView,
  SessionAnalysis
} from '../types'

const base = (orgId: string, projectId: string, versionId: string): string =>
  `/${orgId}/projects/${projectId}/versions/${versionId}`

export const intakesService = {
  /** `GET {base}/intakes` — the version's intakes the caller can read (bare array). */
  async listIntakes(orgId: string, projectId: string, versionId: string): Promise<IntakeRow[]> {
    return apiClient.get<IntakeRow[]>(`${base(orgId, projectId, versionId)}/intakes`)
  },

  /** `GET {base}/intakes/:intakeId` — one intake row. */
  async getIntake(orgId: string, projectId: string, versionId: string, intakeId: string): Promise<IntakeRow> {
    return apiClient.get<IntakeRow>(`${base(orgId, projectId, versionId)}/intakes/${intakeId}`)
  },

  /** `GET {base}/intakes/:intakeId/sessions/:assigneeId/conversation` — the session's transcript. */
  async getSessionConversation(
    orgId: string,
    projectId: string,
    versionId: string,
    intakeId: string,
    assigneeId: string
  ): Promise<ConversationSnapshot> {
    return apiClient.get<ConversationSnapshot>(
      `${base(orgId, projectId, versionId)}/intakes/${intakeId}/sessions/${assigneeId}/conversation`
    )
  },

  /** `GET {base}/intakes/:intakeId/sessions/:assigneeId/analysis` — the session's analysis. */
  async getSessionAnalysis(
    orgId: string,
    projectId: string,
    versionId: string,
    intakeId: string,
    assigneeId: string
  ): Promise<SessionAnalysis> {
    return apiClient.get<SessionAnalysis>(
      `${base(orgId, projectId, versionId)}/intakes/${intakeId}/sessions/${assigneeId}/analysis`
    )
  },

  /** `GET {base}/conversations/:conversationId/conversation` — addressed by conversation id directly. */
  async getConversation(
    orgId: string,
    projectId: string,
    versionId: string,
    conversationId: string
  ): Promise<ConversationSnapshot> {
    return apiClient.get<ConversationSnapshot>(
      `${base(orgId, projectId, versionId)}/conversations/${conversationId}/conversation`
    )
  },

  /** `GET {base}/conversations/:conversationId/progress`. */
  async getConversationProgress(
    orgId: string,
    projectId: string,
    versionId: string,
    conversationId: string
  ): Promise<ProgressView> {
    return apiClient.get<ProgressView>(
      `${base(orgId, projectId, versionId)}/conversations/${conversationId}/progress`
    )
  },

  /** `GET {base}/conversations/:conversationId/attribution`. */
  async getConversationAttribution(
    orgId: string,
    projectId: string,
    versionId: string,
    conversationId: string
  ): Promise<AttributionView> {
    return apiClient.get<AttributionView>(
      `${base(orgId, projectId, versionId)}/conversations/${conversationId}/attribution`
    )
  },

  /**
   * `GET /:orgId/meeting-channels` — ORG-scoped, org-admin only
   * (`@Authenticated(orgAdmin = true)` at the `MeetingChannelController`
   * class level — a non-admin 403s on every handler).
   */
  async listMeetingChannels(orgId: string): Promise<MeetingChannelSurface> {
    return apiClient.get<MeetingChannelSurface>(`/${orgId}/meeting-channels`)
  },

  /** `GET /:orgId/external-captures` — ORG-scoped, org-admin only. */
  async listExternalCaptures(orgId: string): Promise<ExternalCaptureRow[]> {
    return apiClient.get<ExternalCaptureRow[]>(`/${orgId}/external-captures`)
  },

  /**
   * `GET {base}/capture-links` — PROJECT + VERSION scoped (not org-scoped,
   * correcting the ticket's route list), org-admin only.
   */
  async listCaptureLinks(orgId: string, projectId: string, versionId: string): Promise<CaptureLinkRow[]> {
    return apiClient.get<CaptureLinkRow[]>(`${base(orgId, projectId, versionId)}/capture-links`)
  }
}
